/* Nick's Aquarium: keep autoplaying background videos off the critical path.
 *
 * Added 2026-08-23.
 *
 * The homepage carries eleven autoplaying videos - five slideshow slides and six hero banner
 * strips - and every one of them used to start downloading while the page's own HTML, CSS and
 * product images were still arriving. Eleven simultaneous video downloads on one connection is
 * why the first screen took so long to settle.
 *
 * Nothing is removed and nothing is muted or paused: every video still autoplays, loops and
 * looks exactly as it did. The only change is WHEN the download starts.
 *
 *   - Videos within roughly two screens of the top begin as soon as the DOM is ready, which is
 *     effectively when they began before. They carry no poster, so delaying them would show an
 *     empty band.
 *   - Videos further down wait until the shopper is within 800px of them - about a screen of
 *     warning, which is ample for a muted loop to start.
 *
 * The markup opts in by carrying its source in `data-src` instead of `src`, either on the
 * <video> itself or on its <source> children. A video that still has a real `src` is left
 * completely alone, so if Shopify's video_tag output ever changes shape the Liquid falls back to
 * emitting the original eager markup and this script simply finds nothing to do.
 *
 * sections/collection-banner.liquid has its own copy of this logic for the banner video and
 * marks those elements with data-banner-video; they are skipped here so the two never race.
 */
(() => {
  if (window.__deferredAutoplayVideoInit) return;
  window.__deferredAutoplayVideoInit = true;

  /** Start loading one video: promote its deferred source(s), then play. */
  const start = (video) => {
    if (video.dataset.deferredStarted) return;
    video.dataset.deferredStarted = '1';

    let promoted = false;

    if (video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
      delete video.dataset.src;
      promoted = true;
    }

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.setAttribute('src', source.dataset.src);
      source.removeAttribute('data-src');
      promoted = true;
    });

    if (!promoted) return;

    video.load();

    // These are all muted + playsinline, so play() is allowed without a gesture. If a browser
    // refuses anyway the element keeps showing its poster, which is the same fallback as before.
    const played = video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  };

  const collect = () => {
    const found = new Set();
    document.querySelectorAll('video[data-src], video source[data-src]').forEach((node) => {
      const video = node.tagName === 'VIDEO' ? node : node.closest('video');
      // The collection banner runs its own deferral; don't double-handle those.
      if (video && !video.hasAttribute('data-banner-video')) found.add(video);
    });
    return Array.from(found);
  };

  const run = () => {
    const videos = collect();
    if (!videos.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      videos.forEach(start);
      return;
    }

    const nearTop = window.innerHeight * 2;
    const later = [];

    videos.forEach((video) => {
      const top = video.getBoundingClientRect().top;
      // Above, on, or just below the first couple of screens: start now.
      if (top < nearTop) start(video);
      else later.push(video);
    });

    if (!later.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          start(entry.target);
        });
      },
      { rootMargin: '800px 0px' }
    );

    later.forEach((video) => observer.observe(video));
  };

  // Run as soon as the DOM is ready rather than waiting for an idle callback. Anything near the
  // top of the page - the first hero - then starts at essentially the moment it would have before
  // these changes, which matters because those videos carry no poster frame: a deferred one shows
  // an empty band until it arrives. The saving comes from the videos further down, which wait for
  // the observer, and there is no reason to make the visible one pay for that.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
